import type { ToolApprovalCardProps, ToolApprovalRenderer } from "#components/ui/tool-approval/renderers/ToolApprovalRenderer.types.ts";
import { DraftSurface, EditErrors, QuotedMessage, SentText, textEditor, useDraftMode } from "#features/conversations/components/prepared-action/cardControls.tsx";
import { DraftTextArea } from "#features/conversations/components/prepared-action/DraftTextArea.tsx";
import { useTimeZone } from "#contexts/TimeZoneContext.ts";
import { readString } from "#features/conversations/lib/untypedFields.ts";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { H3, Text } from "@hopper-ui/components";
import { IconMessage } from "@intentui/icons";
import "./MessageCard.css";

// Core sends a calendar date, so UTC keeps its day in every time zone. The year shows only when it is not the viewer's
// current year.
function formatFeedbackDay(value: string | null, locale: string, timeZone: string): string | null {
    const date = value === null ? null : new Date(value);
    if (date === null || Number.isNaN(date.getTime())) {
        return null;
    }
    const currentYear = Number(new Intl.DateTimeFormat("en-US", { year: "numeric", timeZone }).format(new Date()));
    const isThisYear = date.getUTCFullYear() === currentYear;

    return new Intl.DateTimeFormat(locale, { month: "short", day: "numeric", year: isThisYear ? undefined : "numeric", timeZone: "UTC" }).format(date);
}

function FeedbackReplyCard(props: ToolApprovalCardProps) {
    const { display, editErrors } = props;
    const { tShared, i18n } = useSharedTranslation();
    const timeZone = useTimeZone();
    const mode = useDraftMode();
    const fields = display ?? {};
    const excerpt = readString(fields, "feedback_excerpt");
    const subject = readString(fields, "feedback_subject");
    const feedbackDate = readString(fields, "feedback_date");
    const feedbackDay = formatFeedbackDay(feedbackDate, i18n.language, timeZone);
    // Core flags anonymous feedback and sends no author name for it.
    const author = fields.is_anonymous === true ? tShared("preparedAction.anonymous") : readString(fields, "author_name");
    const editor = textEditor(props, "reply");

    return (
        <DraftSurface>
            <div className="wai-pa-header">
                <IconMessage className="wai-pa-glyph" aria-hidden="true" />
                <Text size="xs" fontWeight="body-xs-medium" color="neutral-weak" className="wai-pa-label">{tShared("preparedAction.feedbackReply")}</Text>
            </div>

            {subject !== null && (
                <H3 fontSize="body-sm" fontWeight="body-sm-semibold" lineHeight="body-sm" color="neutral" className="wai-pa-title">{subject}</H3>
            )}

            {excerpt !== null && (
                <QuotedMessage
                    author={author ?? tShared("preparedAction.teamMember")}
                    meta={feedbackDay !== null && (
                        <Text size="xs" color="neutral-weak">
                            <time dateTime={feedbackDate ?? undefined}>{tShared("preparedAction.weekOf", { date: feedbackDay })}</time>
                        </Text>
                    )}
                    text={excerpt}
                />
            )}

            {mode === "settled"
                ? <SentText>{editor.value}</SentText>
                : <DraftTextArea editor={editor} label={tShared("preparedAction.yourReply")} isLabelVisible isDisabled={mode === "sending"} />}
            <EditErrors errors={editErrors} />
        </DraftSurface>
    );
}

export const replyToFeedbackRenderer: ToolApprovalRenderer = {
    matches: args => readString(args, "reply") !== null,
    Card: FeedbackReplyCard,
    decisionLabels: { allow: "preparedAction.sendReply", deny: "preparedAction.discard" }
};
